import React, { FC, useState, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Button, Stack, Box as Wrapper, Text, Icon } from '@tonic-ui/react';
import { Modal } from '../../components/modal';
import { useTranslation } from 'react-i18next';

import {
  FormContainer,
  InputField,
  wsPasswordResetValidation,
  Recaptcha,
} from '../../components/forms';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import { getCommonType } from '../../store';
import { resetWSPassword } from '../../services/ds-api';

type WSPasswordResetModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

const WSPasswordResetModal: FC<WSPasswordResetModalProps> = ({ onClose, isOpen }) => {
  const {
    theme: { colorMode },
    locale,
  } = useSelector(
    (state) => ({
      theme: getCommonType(state, 'theme'),
      locale: getCommonType(state, 'locale'),
    }),
    shallowEqual
  );
  const { t } = useTranslation();
  const [formErrors, setFormErrors] = useState({
    error: '',
  });

  const recaptchaRef = useRef();
  const [passwordResetSuccessMsg, setPasswordResetSuccessMsg] = useState('');
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  const handleSubmit = async ({ account, username, recaptcha, setSubmitting, resetForm }) => {
    setDisableSubmitButton(true);
    setFormErrors({
      ...formErrors,
      error: '',
    });
    try {
      const { resetUser, error, ...rest } = await resetWSPassword(account, username, recaptcha);
      if (error) {
        console.error(error);

        setFormErrors({
          ...formErrors,
          error: error,
        });
        setSubmitting(false);
        setDisableSubmitButton(false);
        resetForm();
        recaptchaRef.current.resetRecaptcha();
      } else if (resetUser?.state === 'RESET') {
        setPasswordResetSuccessMsg(t('wsPasswordReset.successMsg'));
      }
    } catch (error) {
      console.error(error);

      setFormErrors({
        ...formErrors,
        error: error.message ? error.message : error,
      });

      setSubmitting(false);
      setDisableSubmitButton(false);
      resetForm();
      recaptchaRef.current.resetRecaptcha();
    }
  };

  return (
    <Modal
      id="ws-forgot-password"
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      header={t('wsPasswordReset.title')}
      body={
        <FormContainer
          id="ws-forgot-password-form"
          customValidation={wsPasswordResetValidation}
          initialValues={{
            account: '',
            username: '',
            recaptcha: '',
          }}
          submitAction={(formPayload) => handleSubmit(formPayload)}
        >
          {passwordResetSuccessMsg ? (
            <Stack direction="row" spacing="3x" margin="20px auto">
              <Icon icon="check-bold" color="#00a94f" />
              <Text>{passwordResetSuccessMsg}</Text>
            </Stack>
          ) : (
            <>
              {formErrors?.error && (
                <NotificationBanner
                  name="ws-forgot-password-form"
                  severity="error"
                  shouldOpen={true}
                  bannerText={formErrors.error}
                />
              )}
              <Text margin="15px 0">{t('wsPasswordReset.instruction')}</Text>
              <Wrapper maxWidth={360} minHeight={350}>
                <Stack spacing="4x">
                  <Wrapper>
                    <InputField
                      id="ws-account"
                      type="text"
                      label={t('wsCommon.account')}
                      name="account"
                      isRequired={true}
                    />
                  </Wrapper>
                  <Wrapper>
                    <InputField
                      id="ws-username"
                      type="username"
                      label={t('wsCommon.username')}
                      name="username"
                      isRequired={true}
                    />
                  </Wrapper>
                  <Wrapper>
                    <Recaptcha
                      ref={recaptchaRef}
                      name="recaptcha"
                      theme={colorMode}
                      siteKey="6Lecq94UAAAAABLMGMKwM5MeNJpnyZhvsbBrjag7"
                      locale={locale}
                    />
                  </Wrapper>
                </Stack>
              </Wrapper>
            </>
          )}
        </FormContainer>
      }
      footer={
        <>
          {!passwordResetSuccessMsg && (
            <Button
              form="c1c_form_ws-forgot-password-form"
              type="submit"
              variant="primary"
              data-id="ws-reset-password-form-submit-btn"
              disabled={disableSubmitButton}
            >
              {t('wsCommon.ok')}
            </Button>
          )}
          <Button width="fit-content" marginLeft="4x" onClick={onClose}>
            {passwordResetSuccessMsg ? t('close') : t('cancel')}
          </Button>
        </>
      }
    />
  );
};

WSPasswordResetModal.displayName = 'WSPasswordResetModal';
export default WSPasswordResetModal;
